import { takeLatest } from 'redux-saga';
import { put, call, fork, select } from 'redux-saga/effects';
import { fromJS } from 'immutable';
import _ from 'lodash';

import { getCampaignTrackedAssets } from '../../utils/jsonapi-functions/campaigns';
import { getTrackedAssetAnalytics } from '../../utils/tracked-assets-analytics-service';
import { getYoutubeVideos, getFacebookPosts, getInstagramPosts, getTwitterPosts, isLoading } from '../../selectors/campaign-analytics-selectors';
import { reportError } from '../../utils/error-handling-service';
import { extractAnalyticsIntoPlatformChannels, getAndFormatTrackedAssetAnalytics, extractAnalyticsTotalData } from '../../containers/analytics-asset-list/sagas';
import { GET_NEW_ASSET_ERROR } from '../../containers/analytics-asset-list/constants';
import { YOUTUBE, FACEBOOK, INSTAGRAM, TWITTER } from '../../utils/static-configuration';
import { SET_ASSET_PAGE_BACK_LINK } from '../../containers/analytics-asset/constants';
import {
  LOAD_CAMPAIGN_ANALYTICS,
  LOAD_CAMPAIGN_ANALYTICS_SUCCESS,
  LOAD_CAMPAIGN_ANALYTICS_ERROR,
  UPDATE_CAMPAIGN_ANALYTICS,
  GET_NEW_CAMPAIGN_ANALYTICS,
  UPDATE_CAMPAIGN_ANALYTICS_TOTALS,
  LOAD_CAMPAIGN_ANALYTICS_SET_LOADING,
} from './constants';

export function* loadCampaignAnalytics(actions) {

  try {
    const analyticsLoading = yield select(isLoading);

    if (!analyticsLoading) {
      yield put({ type: LOAD_CAMPAIGN_ANALYTICS_SET_LOADING, loading: true });

      const campaignId = actions.data.campaignId;
      const trackedAssetsList = yield call(getCampaignTrackedAssets, campaignId);

      const formattedTrackedAssetList = {};
      trackedAssetsList.forEach(trackedAsset => _.set(formattedTrackedAssetList, [trackedAsset.assetId], trackedAsset));

      // Get the data for all trackedAssets
      const trackedAssetsAnalyticsList = yield call(getTrackedAssetAnalytics, trackedAssetsList);

      const youtubeVideos = {};
      const instagramPosts = {};
      const facebookPosts = {};
      const twitterPosts = {};

      const platformOptions = {
        YOUTUBE: youtubeVideos,
        INSTAGRAM: instagramPosts,
        FACEBOOK: facebookPosts,
        TWITTER: twitterPosts,
      };

      extractAnalyticsIntoPlatformChannels(trackedAssetsAnalyticsList, formattedTrackedAssetList, platformOptions);

      const immutableYoutubeVideoList = fromJS(youtubeVideos);
      const immutableInstagramPostList = fromJS(instagramPosts);
      const immutableFacebookPostList = fromJS(facebookPosts);
      const immutableTwitterPostList = fromJS(twitterPosts);

      const hasTrackedDeliverables = (immutableYoutubeVideoList.size > 0 ||
        immutableInstagramPostList.size > 0 ||
        immutableFacebookPostList.size > 0 ||
        immutableTwitterPostList.size > 0);

      const youtubeTotals = extractAnalyticsTotalData(immutableYoutubeVideoList, YOUTUBE);
      const instagramTotals = extractAnalyticsTotalData(immutableInstagramPostList, INSTAGRAM);
      const facebookTotals = extractAnalyticsTotalData(immutableFacebookPostList, FACEBOOK);
      const twitterTotals = extractAnalyticsTotalData(immutableTwitterPostList, TWITTER);

      yield put({
        type: LOAD_CAMPAIGN_ANALYTICS_SUCCESS,
        youtubeVideos: immutableYoutubeVideoList,
        instagramPosts: immutableInstagramPostList,
        facebookPosts: immutableFacebookPostList,
        twitterPosts: immutableTwitterPostList,
        hasTrackedDeliverables,
        youtubeTotals: fromJS(youtubeTotals),
        instagramTotals: fromJS(instagramTotals),
        facebookTotals: fromJS(facebookTotals),
        twitterTotals: fromJS(twitterTotals),
      });

      yield put({ type: SET_ASSET_PAGE_BACK_LINK, backLink: `/campaign/${campaignId}?analytics` });
    }

  } catch(error) {
    reportError('LOAD_CAMPAIGN_ANALYTICS_ERROR', error.message);
    yield put({ type: LOAD_CAMPAIGN_ANALYTICS_ERROR, error: error.message });
  }
}

export function* addTrackedAssetToStateList(actions) {
  try {
    const { trackedAsset } = actions.data;

    const updatedTrackedAsset = yield call(getAndFormatTrackedAssetAnalytics, trackedAsset);

    if (updatedTrackedAsset) {
      let assetList = null;
      if (updatedTrackedAsset.platform === YOUTUBE) assetList = 'youtubeVideos';
      if (updatedTrackedAsset.platform === INSTAGRAM) assetList = 'instagramPosts';
      if (updatedTrackedAsset.platform === FACEBOOK) assetList = 'facebookPosts';
      if (updatedTrackedAsset.platform === TWITTER) assetList = 'twitterPosts';

      yield put({
        type: UPDATE_CAMPAIGN_ANALYTICS,
        newAsset: fromJS(updatedTrackedAsset),
        assetId: trackedAsset.assetId,
        assetList,
      });

      const youtubeVideos = yield select(getYoutubeVideos);
      const instagramPosts = yield select(getInstagramPosts);
      const facebookPosts = yield select(getFacebookPosts);
      const twitterPosts = yield select(getTwitterPosts);

      const youtubeTotals = extractAnalyticsTotalData(youtubeVideos, YOUTUBE);
      const instagramTotals = extractAnalyticsTotalData(instagramPosts, INSTAGRAM);
      const facebookTotals = extractAnalyticsTotalData(facebookPosts, FACEBOOK);
      const twitterTotals = extractAnalyticsTotalData(twitterPosts, TWITTER);

      yield put({
        type: UPDATE_CAMPAIGN_ANALYTICS_TOTALS,
        youtubeTotals: fromJS(youtubeTotals),
        instagramTotals: fromJS(instagramTotals),
        facebookTotals: fromJS(facebookTotals),
        twitterTotals: fromJS(twitterTotals),
      });
    }
  } catch (error) {
    reportError('ADD_TRACKED_ASSET_TO_CAMPAIGN_ERROR', error.message);
    yield put({ type: GET_NEW_ASSET_ERROR, error });
  }
}

export function* watchLoadCampaignAnalytics() {
  yield takeLatest(LOAD_CAMPAIGN_ANALYTICS, loadCampaignAnalytics);
}

export function* watchGetNewCampaignTrackedAsset() {
  yield takeLatest(GET_NEW_CAMPAIGN_ANALYTICS, addTrackedAssetToStateList);
}

export default function* campaignAnalyticsSagas() {
  yield [
    fork(watchLoadCampaignAnalytics),
    fork(watchGetNewCampaignTrackedAsset),
  ];
}



/** WEBPACK FOOTER **
 ** ./src/containers/campaign-analytics/sagas.js
 **/